import { FC, useEffect, useState } from "react"
import { FormContainer, useForm } from "react-hook-form-mui"
import { IFormProps } from "@/core/interfaces"
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  Grid,
  Stack,
  Typography,
} from "@mui/material"
import { Modal, SnackbarAlert } from "@/core/components"
import { TgMailing } from "../../../interfaces/mailing"
import { usePagedEnqueueRun } from "../usePagedEnqueueRun"
import { buildPagedRequest } from "./buildRecipients"
import { MethodSelectField } from "../methodSelectField"
import { CSVField } from "./csvField"
import { CSVPreview } from "./csvPreview"
import { CsvColsToBodyFieldsCollation } from "./csvColsToBodyFieldCollation"
import { BodyFieldsPropperties } from "../../../constants"
import { CsvStepper } from "./csvStepper"
import { Name } from "../name"
import { ScheduleDate } from "../scheduleDate"
import { Speed } from "../speed"
import { Comment } from "../comment"
import { Type } from "../type"
import { DedupByUserId } from "../dedupByUserId"

export type CSVRow = Record<string, string>
interface IProps extends IFormProps<void> {}
export type IMailingFormData<
  M extends TgMailing.AvailableMethods = TgMailing.AvailableMethods,
> = Omit<TgMailing.CreateMailingData<M>, "body" | "token" | "messages"> & {
  collation: Record<keyof (typeof BodyFieldsPropperties)[M], string>
  rows: CSVRow[]
  method: M
}

export const MailingCsvForm: FC<IProps> = (props) => {
  const {
    phase,
    enqueued,
    errorText,
    mailingLive,
    resubmitBlocked,
    cancelErrorId,
    run,
    retry,
    stop,
    clearCancelError,
  } = usePagedEnqueueRun()
  const isBusy = phase === "uploading" || phase === "paused"

  const [formError, setFormError] = useState<string | null>(null)
  const [finishConfirmOpen, setFinishConfirmOpen] = useState(false)
  const [successSnackbarOpen, setSuccessSnackbarOpen] = useState(false)

  const formContext = useForm<IMailingFormData>({
    defaultValues: {
      method: "sendMessage",
    },
  })

  useEffect(() => {
    if (phase === "success") {
      formContext.reset()
      setSuccessSnackbarOpen(true)
    }
  }, [phase, formContext])

  const scheduleDate = formContext.watch("scheduleDate")
  const recipientCount = (formContext.watch("rows") ?? []).length

  const onSubmit = (data: IMailingFormData) => {
    try {
      setFormError(null)
      const { request, recipients, useMtProto } = buildPagedRequest(data)
      run({ request, recipients, useMtProto })
    } catch (e) {
      setFormError(e instanceof Error ? e.message : String(e))
    }
  }

  return (
    <FormContainer<IMailingFormData>
      onSuccess={onSubmit}
      formContext={formContext}
    >
      <SnackbarAlert
        isOpen={successSnackbarOpen}
        onClose={() => setSuccessSnackbarOpen(false)}
        variant="success"
        title="Успешно"
        message="Рассылка создана, отправка идёт"
      />
      <SnackbarAlert
        isOpen={!!formError}
        onClose={() => setFormError(null)}
        variant="error"
        title="Произошла ошибка"
        message={formError ?? ""}
      />
      <SnackbarAlert
        isOpen={!!cancelErrorId}
        onClose={clearCancelError}
        variant="error"
        message={`Не удалось остановить рассылку. Отмените её вручную из списка активных (id: ${cancelErrorId}).`}
      />
      {phase === "uploading" && (
        <Alert severity="info" icon={<CircularProgress size={16} />}>
          Загрузка получателей: {enqueued.toLocaleString("ru-RU")}.{" "}
          {scheduleDate
            ? "Отправка начнётся по расписанию; загрузка идёт уже сейчас."
            : "Отправка уже идёт во время загрузки — часть аудитории задействована до завершения сборки."}
        </Alert>
      )}
      {phase === "paused" && (
        <Alert severity="warning">
          {errorText}
          <Box mt={1} display="flex" gap={1}>
            <Button size="small" variant="contained" onClick={retry}>
              Повторить
            </Button>
            <Button
              size="small"
              color="warning"
              onClick={() => setFinishConfirmOpen(true)}
            >
              Остановить и сохранить отчёт
            </Button>
            <Button size="small" color="error" onClick={() => void stop(false)}>
              Удалить рассылку
            </Button>
          </Box>
        </Alert>
      )}
      {finishConfirmOpen && (
        <Modal
          disableBackdropClose
          title="Остановить и сохранить отчёт?"
          onClose={() => setFinishConfirmOpen(false)}
          element={
            <Box>
              <Typography>
                Часть аудитории уже получила сообщения — отправка идёт с первой
                загруженной страницы. Рассылка будет остановлена, отчёт
                сохранится как частичный.
              </Typography>
              <Box mt={2} display="flex" justifyContent="flex-end" gap={1}>
                <Button onClick={() => setFinishConfirmOpen(false)}>
                  Продолжить загрузку
                </Button>
                <Button
                  color="warning"
                  variant="contained"
                  onClick={() => {
                    setFinishConfirmOpen(false)
                    void stop(true)
                  }}
                >
                  Остановить
                </Button>
              </Box>
            </Box>
          }
        />
      )}
      {phase === "failed" && (
        <Alert severity="error">
          {errorText}
          {mailingLive && (
            <Box mt={1}>
              <Button
                size="small"
                color="error"
                onClick={() => void stop(false)}
              >
                Отменить рассылку
              </Button>
            </Box>
          )}
        </Alert>
      )}
      <Grid container spacing={1}>
        <Grid item xs={12} mb={4} mt={1}>
          <CsvStepper />
        </Grid>
        <Grid item xs={12} md={6}>
          <Stack spacing={2}>
            <Name<IMailingFormData> required />
            <Type<IMailingFormData> />
            <DedupByUserId<IMailingFormData> />
            <ScheduleDate<IMailingFormData> />
            <Speed<IMailingFormData> recipientCount={recipientCount} />
            <Comment<IMailingFormData> />
            <CSVField />
          </Stack>
        </Grid>
        <Grid item xs={12} md={6}>
          <Stack spacing={2}>
            <MethodSelectField<IMailingFormData> />
            <CsvColsToBodyFieldsCollation />
          </Stack>
        </Grid>
        <Grid item xs={12} mt={3}>
          <Box maxHeight={400} height={"100%"}>
            <CSVPreview />
          </Box>
        </Grid>
      </Grid>
      <Divider light sx={{ marginY: 2 }} />
      <Box display="flex" justifyContent="end">
        <Button
          disabled={isBusy || resubmitBlocked}
          type="submit"
          variant="contained"
        >
          Отправить&nbsp;{isBusy && <CircularProgress size={20} />}
        </Button>
      </Box>
    </FormContainer>
  )
}
